import React, { ReactNode, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CONFIG } from '../config/mainConfig';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { setCurrentMainURLQuery, setCurrentPathname, setCurrentSearchQuery } from '../store/appSlice';
import { setRedirectURL } from '../store/userSlice';
import { getURLSearch } from '../helper/helper';
import { Toast } from '../components/Common/Toast';
import { UserLocalStorageKeysEnum } from '../interfaces/user.interface';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { TokenCookieManager } from '../utils/tokenCookies';
import ImpersonationIndicator from '../components/ImpersonationIndicator/ImpersonationIndicator';
import { shouldUseImpersonationAuth, clearImpersonationSession, isImpersonationTab } from '../utils/impersonation.utils';

const UserRouteGuard = ({ children }: { children?: ReactNode }) => {
  return <BrowserPopupComponent>{children || <Outlet />}</BrowserPopupComponent>;
};

export default UserRouteGuard;

// Or, a simpler version assuming the hash starts with `/?`
export const getQueryParamSimple = (paramName: string, url: string): string | null => {
  const queryString = url?.includes('?') ? url.split('?')[1] : '';
  const searchParams = new URLSearchParams(queryString);

  return searchParams.get(paramName);
};

export const handleUnload = (event: any) => {
  event.preventDefault();
  event.returnValue = '';
};

interface BrowserPopupComponentProps {
  children: ReactNode;
  ignorePopup?: boolean;
  ignoreAuthorisationGuard?: boolean;
}

export const BrowserPopupComponent = ({
  children,
  ignorePopup,
  ignoreAuthorisationGuard = false,
}: BrowserPopupComponentProps): React.JSX.Element => {
  const location = useLocation();
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const redirectURL = useSelector((state: RootState) => state.user.redirectURL);
  const userAuthorized = useSelector((state: RootState) => state.user.userAuthorized);

  useEffect(() => {
    const search = getURLSearch(true);
    console.log(search);
    if (search) {
      const queryParams = new URLSearchParams(search);
      console.log(queryParams.get('error'));
      if (queryParams.get('error')) {
        setTimeout(() => Toast(queryParams.get('error')?.toString(), true));
      }
    }
    const pathname = location.pathname;
    // Allow impersonation sessions to bypass normal auth checks
    const isImpersonating = shouldUseImpersonationAuth();

    if (
      !ignoreAuthorisationGuard &&
      !isImpersonating &&
      !isImpersonationTab() &&
      ((userAuthorized !== null && !userAuthorized) || !TokenCookieManager.hasToken())
    ) {
      if (pathname?.includes('classroom') || pathname?.includes('teacher')) {
        navigateTo('/auth/teacher', { replace: true });
      } else {
        navigateTo('/', { replace: true });
      }
      signOut(auth).then(() => {
        window.removeEventListener('beforeunload', handleUnload);

        TokenCookieManager.removeToken();
        localStorage.removeItem(UserLocalStorageKeysEnum.CURRENT_USER);
        localStorage.removeItem(UserLocalStorageKeysEnum.BOOK_ID);
        localStorage.removeItem(UserLocalStorageKeysEnum.EDIT_MODE);
        localStorage.removeItem(UserLocalStorageKeysEnum.STORY_STEP);
        localStorage.removeItem(CONFIG.PROMO_PLAN);
      });
    }
  }, []);

  useEffect(() => {
    if (redirectURL && redirectURL.trim() !== '') {
      dispatch(setRedirectURL(''));
      navigateTo('/redirect?loc=' + encodeURIComponent(redirectURL), { replace: true });
    }
  }, [redirectURL, dispatch, navigateTo]);

  useEffect(() => {
    dispatch(setCurrentPathname(location.pathname));
    dispatch(setCurrentSearchQuery(location.search));
    dispatch(setCurrentMainURLQuery(getURLSearch(false)));
    const popup = getQueryParamSimple(CONFIG.BROWSER_REFRESH_POPUP_PARAM, location.search);
    if (popup === 'no' || ignorePopup) {
      try {
        window.removeEventListener('beforeunload', handleUnload);
      } catch (err) {}
      return;
    }

    const hasActiveWork = !!localStorage.getItem(UserLocalStorageKeysEnum.BOOK_ID) &&
      !!localStorage.getItem(UserLocalStorageKeysEnum.STORY_STEP);
    if (hasActiveWork) {
      window.addEventListener('beforeunload', handleUnload);
    }

    return () => window.removeEventListener('beforeunload', handleUnload); // Cleanup
  }, [location]);

  return (
    <div>
      <ImpersonationIndicator />
      <div style={{ paddingTop: shouldUseImpersonationAuth() ? '3.5rem' : '0' }}>
        {children}
      </div>
    </div>
  );
};
